import { useCallback, useEffect, useState } from 'react';

// The full GPS window plays back over this wall-clock duration when "play" is
// engaged — chosen so a typical multi-hour shift replays in ~20 seconds.
const PLAYBACK_DURATION_MS = 20_000;
// How often the playback loop advances the replay time while playing.
const PLAYBACK_TICK_MS = 60;

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(value, max));
}

/**
 * Owns the single controlled `replayTimeMs` that drives BOTH the map markers and
 * the Gantt indicator. The Gantt feeds it on pointer drag (via `setReplayFraction`)
 * and the map reads `replayTimeMs` to interpolate each truck's position.
 *
 * `replayTimeMs` is always clamped to `[minTimeMs, maxTimeMs]`, including when the
 * window itself changes (e.g. when the ping set finishes loading).
 */
export default function useReplayScrubber(minTimeMs: number, maxTimeMs: number) {
  const [replayTimeMs, setReplayTimeMsRaw] = useState(minTimeMs);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLocked, setIsLockedRaw] = useState(false);

  const setIsLocked = useCallback((locked: boolean) => {
    setIsLockedRaw(locked);
    if (locked) setIsPlaying(false);
  }, []);

  const setReplayTimeMs = useCallback(
    (timeMs: number) => setReplayTimeMsRaw(clamp(timeMs, minTimeMs, maxTimeMs)),
    [minTimeMs, maxTimeMs],
  );

  const setReplayFraction = useCallback(
    (fraction: number) =>
      setReplayTimeMs(minTimeMs + clamp(fraction, 0, 1) * (maxTimeMs - minTimeMs)),
    [minTimeMs, maxTimeMs, setReplayTimeMs],
  );

  // Re-clamp when the window shifts (data finished loading, order changed, etc.).
  useEffect(() => {
    setReplayTimeMsRaw((prev) => clamp(prev, minTimeMs, maxTimeMs));
  }, [minTimeMs, maxTimeMs]);

  const togglePlay = useCallback(() => {
    setIsPlaying((playing) => {
      const next = !playing;
      if (next) {
        setIsLockedRaw(false);
        // Restart from the beginning if play is pressed at the end of the window.
        if (replayTimeMs >= maxTimeMs) {
          setReplayTimeMsRaw(minTimeMs);
        }
      }

      return next;
    });
  }, [maxTimeMs, minTimeMs, replayTimeMs]);

  // Auto-advance while playing; stop when the end of the window is reached.
  useEffect(() => {
    if (!isPlaying) return;

    const span = maxTimeMs - minTimeMs;
    if (span <= 0) {
      setIsPlaying(false);

      return;
    }

    const stepMs = (span / PLAYBACK_DURATION_MS) * PLAYBACK_TICK_MS;
    const interval = setInterval(() => {
      setReplayTimeMsRaw((prev) => {
        const next = prev + stepMs;
        if (next >= maxTimeMs) {
          setIsPlaying(false);

          return maxTimeMs;
        }

        return next;
      });
    }, PLAYBACK_TICK_MS);

    return () => clearInterval(interval);
  }, [isPlaying, maxTimeMs, minTimeMs]);

  const replayFraction =
    maxTimeMs > minTimeMs ? (replayTimeMs - minTimeMs) / (maxTimeMs - minTimeMs) : 0;

  return {
    isLocked,
    isPlaying,
    replayFraction,
    replayTimeMs,
    setIsLocked,
    setReplayFraction,
    setReplayTimeMs,
    togglePlay,
  };
}
